import { Link } from "react-router-dom";
import type { Topic } from "@/types/content";
import { categories } from "@/mocks/site";
import { formatCount } from "@/utils/format";

function categoryName(id: string): string {
  return categories.find((c) => c.id === id)?.name ?? "热议";
}

type TopicCardProps = {
  topic: Topic;
  rank?: number;
  variant?: "default" | "compact";
};

export default function TopicCard({ topic, rank, variant = "default" }: TopicCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/topic/${topic.id}`}
        className="group flex gap-3 py-3 border-b border-background-200 last:border-b-0"
      >
        {typeof rank === "number" && (
          <span
            className={`w-6 h-6 shrink-0 rounded-md text-xs font-black flex items-center justify-center ${
              rank <= 3
                ? "bg-primary-500 text-background-50"
                : "bg-background-200 text-foreground-600"
            }`}
          >
            {rank}
          </span>
        )}
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {topic.title}
          </h3>
          <p className="mt-1.5 text-xs text-foreground-500">
            {formatCount(topic.views)} 浏览 · {topic.commentCount} 条讨论
          </p>
        </div>
      </Link>
    );
  }

  return (
    <article className="group h-full flex flex-col rounded-2xl overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors">
      <Link to={`/topic/${topic.id}`} className="block relative">
        <div className="w-full h-44 md:h-48 overflow-hidden bg-background-200">
          <img
            src={topic.cover}
            alt={`${topic.title} 话题配图`}
            title={`${topic.title} - 爆料91热议话题`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-background-50/90 backdrop-blur text-[11px] font-bold text-primary-700">
          <i className={categories.find((c) => c.id === topic.category)?.icon ?? "ri-fire-line"}></i>
          {categoryName(topic.category)}
        </span>
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-[11px] font-bold">
          <i className="ri-fire-fill"></i>
          热度 {topic.heat}
        </span>
      </Link>

      <div className="flex flex-col flex-1 p-4 md:p-5">
        <Link to={`/topic/${topic.id}`}>
          <h3 className="text-base md:text-lg font-bold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {topic.title}
          </h3>
        </Link>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2 flex-1">
          {topic.summary}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {topic.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-900 text-[11px] font-medium"
            >
              #{tag}
            </span>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line"></i>
            {topic.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatCount(topic.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {topic.commentCount}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}